import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSmartState from '../../../common/hooks/useSmartState';
import { getStudentImage } from '../../../common/styles/Images';
import InitialsAvatar, { hasProfilePicture } from '../../../library/SimpleControls/InitialsAvatar';
import { SmartContext } from '../../../library/Core/SmartContext';
import { getDomainValueForCode, isEmpty, handleCopyText, getFlexColDomainValueForCode } from '../../../library/Core/SmartFunctions';
import { FormControl, FormSection } from '../../../library/Core/SmartTypes';
import ActionSettings from '../../../library/SimpleControls/ActionSettings';
import * as XLSX from 'xlsx';
import BasAdminReportService from './BasAdminReportService';
import useInternalAdminReports from './useInternalAdminReports';
import SessionContext from '../../../library/Core/SessionContext';
import AssignSession from '../../student/view/sections/AssignSession';
import { REPORT_CONFIG } from '../reports/report-config';
import { axiosClient } from '../../../library/Service/axiosClient';
import { Modal, Button } from 'react-bootstrap';   
import moment from 'moment';
import Swal from 'sweetalert2';
import SendMessage from '../../student/view/sections/SendMessage';

const BATCH_SIZE = 10;

const Results = () => {
    const { state, dispatch } = useContext(SmartContext);
    const allRowsData = state.data.alumniList ?? [];
    const [selectedTest, setSelectedTest] = useState('');
    const [showTestModal, setShowTestModal] = useState(false);

    const [bulkAction, setBulkAction] = useState<string>('' as any);
    const { setStateValue } = useSmartState();
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const { getFormattedDate, activateAlumni, deactivateAlumni, validateAlumni,verifyEmail, deleteUser } = useInternalAdminReports();
    const resultsFormConfig = state.formConfig?.sectionRepository[1] as FormSection;
    const config = REPORT_CONFIG[state.routeInfo.pageName];
    const loadMoreRef = useRef(null);
    const [pageData, setPageData] = useState([] as any[]);
    const getActionLabel = (code: string) => code.toLowerCase().replace(/_/g, ' ');
    const navigate = useNavigate();
    const { sessionState } = useContext(SessionContext);

    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    
    const [counsellors, setCounsellors] = useState([]);

    const handleSelectRow = (event: any) => {
        if (event.target.checked) setSelectedRows((prev) => [...prev, event.target.value]);
        else setSelectedRows((prev) => prev.filter((id) => id !== event.target.value));
    };

    const handleSelectAll = (event: any) => {
        const students = event.target.checked ? allRowsData.map((item: any) => item.uuid) : [];
        setSelectedRows(students);
    };

    const getTagline = (row: any) => {
        if (!isEmpty(row.twelvethPassoutYear)) {
            return `${row.programName} - ${row.twelvethPassoutYear}`;
        } else if (!isEmpty(row.programName)) {
            return `${row.programName}`;
        } else {
            return '';
        }
    };

    useEffect(() => {
        setPageData([...allRowsData.slice(0, BATCH_SIZE)]);
    }, [state.data.alumniList]);

    useEffect(() => {
        const getCounsellors = async () => {
            const counsellorResponse = await axiosClient().post(`${process.env.REACT_APP_BAS_API}/bas/get-counsellors-list`);
            if(counsellorResponse.status == 200){
                setCounsellors(counsellorResponse?.data?.data);
            }
        };
        getCounsellors();
    }, []);

    useEffect(() => {
        const loadMoreObserver = new IntersectionObserver((entries) => {
            if (allRowsData.length <= pageData.length) {
                if (loadMoreRef.current) loadMoreObserver.unobserve(loadMoreRef.current);
                return;
            }

            if (entries[0].isIntersecting) {
                const nextBatch = allRowsData.slice(0, pageData.length + BATCH_SIZE);
                setPageData([...nextBatch]);
            }
        });

        if (loadMoreRef.current) loadMoreObserver.observe(loadMoreRef.current);

        return () => {
            if (loadMoreRef.current) {
                loadMoreObserver.unobserve(loadMoreRef.current);
            }
        };
    }, [loadMoreRef, pageData]);

    const getAlumniNameField = (row: any) => {
        return (

            <div className="d-flex flex-column checkoption">
                {config['SHOW_SELECT_ALL'] && (
                    <input
                        className="form-check-input me-2 StudentId"
                        name="uuid"
                        type="checkbox"
                        value={row.uuid}
                        checked={selectedRows.includes(row.uuid)}
                        onChange={handleSelectRow}
                    />
                )}
                <a
                    className="tablink"
                    href={`/alumni/${row.uuid}/profile`}
                    target="_blank"
                    rel="noopener noreferrer">
                    <i className="me-2 position-relative">
                        {hasProfilePicture(row.profilePictureFilePath) ? (
                            <img src={getStudentImage(row)} className="rounded-circle img-fluid" width="30" />
                        ) : (
                            <InitialsAvatar name={row.name} firstName={row.firstName} lastName={row.lastName} size={30} />
                        )}
                        {showStar(row) && <i className="icon-star circle-img"></i>}
                    </i>
                    <span
                        data-bs-toggle="tooltip"
                        title={`${row.name}`}
                        className="singlelinename font-16 lineHeight-16">
                        {row.name}
                        <span className="d-block font-13 fst-italic"> {getTagline(row)} </span>
                    </span>
                </a>
                <div className="copyId d-flex align-items-center mt-1">
                    <span className="font-13 fst-italic singlelinename me-1"> {row.uuid} </span>
                    <button
                        id={`uuid-${row.uuid}`}
                        className="copy-text border-0 bg-transparent"
                        title="click to copy id"
                        onClick={() => handleCopyText(row.uuid, `uuid-${row.uuid}`)}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-copy"
                            viewBox="0 0 16 16">
                            <path
                                fill-rule="evenodd"
                                d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        );
    };

    const getShortenedSchoolName = (row: any): any => {
        if (!row?.schoolName || typeof row.schoolName !== 'string') {
            // Gracefully handle missing/invalid schoolName
            return <span style={{ color: 'gray' }}>N/A</span>;
        }
        const parts = row?.schoolName.split(',');
        const schoolNameContent =
            parts.length > 1 ? (
                <>
                    <span>
                        {parts[0]},<br />
                        {parts.slice(1).join(',').trim()}
                    </span>
                </>
            ) : (
                <>{row.schoolName}</>
            );

        // If condition is not satisfied, display just the school name text
        return schoolNameContent;
    };

    const [showCloseCaseModal, setShowCloseCaseModal] = useState(false);
    const [comment, setComment] = useState('');
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [selectedTestData,setSelectedTestData]=useState<any>(null);

const handleCloseCase = async () => {
    try {
      const response = await axiosClient().post(`${process.env.REACT_APP_BAS_API}/counsellorsession/close-case-update`, {
        id: selectedUser,
        status: 1,
        message: comment,
      });
  
      toast.success(response.data.message || 'Case closed successfully!');
      setShowCloseCaseModal(false);
      setComment('');
    
      // Refresh list
      BasAdminReportService.search(state.data.searchCriteria, state.routeInfo.pageName).then((response: any) => {
        setStateValue('alumniList', response.data);
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to close the case.');
    }
  };
  

    const getActionNode = (control: FormControl, row: any) => {
        const actions = config['ROW_ACTION'].filter((action: any) => {  
            if(!['SCHEDULE','RE-SCHEDULE', 'ON-DEMAND_SESSION'].includes(action)) return true; // Skip if no action is defined

            const latestSession = row?.counsellingSessions?.reduce((maxSession: any, session: any) => {
            return !maxSession || session.stage > maxSession.stage ? session : maxSession;
            }, null);

            // UAS-1967-start @dt 28th.Oct,2K25 @by RG
            // if(!latestSession) return action === 'SCHEDULE' ? true : false; // If no session, allow scheduling
            if(!latestSession) return ['SCHEDULE', 'ON-DEMAND_SESSION'].includes(action) ? true : false; // If no session, allow scheduling
            // UAS-1967-end

            const appointmentFrom = !isEmpty(latestSession) ? new Date(new Date(latestSession?.appointmentFrom).getTime() + 330 * 60000) : new Date();
            const appointmentTo = !isEmpty(latestSession) ? new Date(new Date(latestSession?.appointmentTo).getTime() + 330 * 60000) : new Date();

            //   console.log(appointmentFrom,  'appointmentFrom',appointmentTo, 'appointmentTo');
            let flag = false;

            if(row.basCloseCase === 1) return false; // If case is closed, no actions allowed
            
            // UAS-1967-start @dt 28th.Oct,2K25 @by RG
            // if((latestSession && !['SCHEDULED','COMPLETED'].includes(latestSession?.status) && ((new Date(appointmentTo).getTime() < new Date().getTime()) || ['CANCELLED'].includes(latestSession?.status))) && action === 'SCHEDULE') {
            if(
                latestSession
                && !['SCHEDULED','COMPLETED'].includes(latestSession?.status) 
                && (
                    new Date(appointmentTo).getTime() < new Date().getTime()
                    || ['CANCELLED'].includes(latestSession?.status)
                )
                && ['SCHEDULE', 'ON-DEMAND_SESSION'].includes(action)
            ) {
            // UAS-1967-end
            flag = true;
            }

            if((latestSession && ['SCHEDULED','RE-SCHEDULED'].includes(latestSession?.status) && ((new Date(appointmentFrom).getTime() - new Date().getTime()) > 60 * 60 * 1000)) && action === 'RE-SCHEDULE') {
            flag = true;
            }
            return flag;
        });

      return row.basCloseCase === 1 ? (
        <span style={{ color: 'red', fontWeight: 'bold' }}>Closed Case</span>
      ) : actions.length > 0 ? (
        <ActionSettings
          id={row}
          actions={actions}
          onClick={handleAction}
          onCloseCase={(row: { uuid: string }) => {
            setSelectedUser(row.uuid);
            setComment('');
            setShowCloseCaseModal(true);
          }}
        />
      ) : null;
      
    }
      
    const handleAction = (row: any, action: string) => {
        if (action === 'CLOSE_CASE') {
          setSelectedUser(row.uuid);
          setComment('');
          setShowCloseCaseModal(true);
        } else {
          handlePerformAction(action, [row]);
        }
      };

  const getParentInfo = (row: any): any => {
    const schoolNameContent = row?.parentDetails ? (
        <>
            {row.parentDetails.split(',').map((part: string, index: number, array: string[]) => (
                <div key={index}>
                    <a
                        href={`/alumni/${row?.uuid}/profile/parent-info`}
                        target="_blank"
                        rel=""
                        style={{ textDecoration: 'none' }} 
                    >
                        {part.trim()}
                    </a>
                    {index < array.length - 1 && ','}
                </div>
            ))}
        </>
    ) : (
        <>N/A</>
    );
    return schoolNameContent;
};

    const getTestApplicable = (row: any): JSX.Element => {
        const baseUrl = `${process.env.REACT_APP_APP_BASE_URL}/`;

    const getStatus = (sessions: any[]): JSX.Element[] => {
        const statusMap: Record<string, { status: string; item: any }> = {};
        const filteredSessions = sessions.filter((s) => s.testCode !== 1);

        filteredSessions.forEach((session) => {
        const { testName, startDate, endDate } = session;
        const hasStart = !!startDate;
        const hasEnd = !!endDate;

        if (!statusMap[testName]) {
            let status = 'Not Taken';
            if (hasEnd) status = 'Completed';
            else if (hasStart) status = 'Started';
            statusMap[testName] = { status, item: session };
        } else {
            const currentStatus = statusMap[testName].status;
            if (currentStatus === 'Not Taken' && hasStart) {
            statusMap[testName] = { status: 'Started', item: session };
            }
            if (currentStatus !== 'Completed' && hasEnd) {
            statusMap[testName] = { status: 'Completed', item: session };
            }
        }
        });

        return Object.entries(statusMap).map(([name, { status, item }]) => {
        let encodedPath = '';
        const testCode = item.testCode;

        switch (testCode) {
            case 101:
            encodedPath = 'cHN5Y2hvbWV0cmljX3Rlc3QvY2FyZWVyR3VpZGUvTGVhcm5pbmdzdHlsZXRlc3Q=';
            break;
            case 100:
            encodedPath = 'cHN5Y2hvbWV0cmljX3Rlc3QvY2FyZWVyR3VpZGUvTXVsdGlwbGVJbnRlbGxpZ2VuY2VUZXN0';
            break;
            case 104:
            encodedPath = 'cHN5Y2hvbWV0cmljX3Rlc3QvY2FyZWVyR3VpZGUvUGVyc29uYWxpdHlUZXN0';
            break;
            case 115:
            encodedPath = 'cHN5Y2hvbWV0cmljX3Rlc3QvY2FyZWVyR3VpZGUvU3RyZWFtU2VsZWN0b3JUZXN0';
            break;
            case 114:
            encodedPath = 'cHN5Y2hvbWV0cmljX3Rlc3QvY2FyZWVyR3VpZGUvSWRlYWxjYXJlZXJ0ZXN0';
            break;
            default:
            encodedPath = btoa('profile');
        }

        const userHash = row?.userHash || '';
        const profileLink = `${baseUrl}sso/${userHash}/${encodedPath}`;

        return (
            <div key={name} className='d-flex flex-wrap mb-2'>
            <strong
                className={`d-block w-100 ${status === 'Completed' ? 'blue-text' : 'disabled'}`}
                style={{ cursor: 'pointer' }}
                onClick={async () => {
                    setSelectedTest(name);
                    // if (status === 'Completed' && testCode !== 104) {
                    if (status === 'Completed') {
                    try {
                        const response = await BasAdminReportService.fetchTestSessions(row.uuid);
                            if (response?.data?.success && Array.isArray(response.data?.data)) {
                            const testData = response.data?.data.find(
                                (t: any) => t.test_type == testCode
                            );
                            let jsonData = null;
                                try {
                                if (testData?.key2succeed_json) {
                                    jsonData = JSON.parse(testData.key2succeed_json);
                                } else if (testData?.brainwonders_json) {
                                    jsonData = JSON.parse(testData.brainwonders_json);
                                }
                                } catch (err) {
                                console.error("Failed to parse test JSON", err);
                                }
                            setSelectedTestData(jsonData);
                            setShowTestModal(true);
                            }
                        } catch (err) {
                            console.error('Error fetching test data', err);
                            alert('Failed to load test data. Please try again.');
                        }
                    }
                }}
                >
                {name}:
                </strong>

            <span className='me-2'>{status}</span>
            {(status === 'Completed' || status === 'Started' || status === 'Not Taken') && (
                <i
                className='bi bi-copy font-14'
                style={{ color: '#007bff', cursor: 'pointer' }}
                title='Copy Test Link'
                onClick={() => {
                    navigator.clipboard.writeText(profileLink);
                    alert('Test link copied to clipboard!');
                }}
                ></i>
            )}
            </div>
        );
        });
    };

    const sessions = row?.psychometricSessions?.filter((s: any) => s.testCode !== 1);

    if (Array.isArray(sessions) && sessions.length > 0) {
        return <div>{getStatus(sessions)}</div>;
    }

    return <span style={{ color: 'gray' }}>No Tests Assigned</span>;
};


    const renderBookingLink = (bookingUrl: string, id: string | number) => (
    <div className="d-flex align-items-center mt-1">
        <a
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="me-2"
        >
        Booking Link
        </a>

        <button
        id={`copy-booking-${id}`}
        className="copy-text border-0 bg-transparent"
        title="Click to copy booking link"
        onClick={() => handleCopyText(bookingUrl, `copy-booking-${id}`)}
        >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-copy"
            viewBox="0 0 16 16"
        >
            <path
            fillRule="evenodd"
            d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
            />
        </svg>
        </button>
    </div>
    );

 const getCounsellingInfo = (row: any): JSX.Element => {
    const sessions = row?.counsellingSessions || [];
    const bookingUrl = `${process.env.REACT_APP_APP_BASE_URL}/sso/${row?.userHash}/YmFz`;

    const maxSentStage = sessions
        .filter((s: any) => s.status?.toUpperCase() === "SD_SENT")
        .reduce((max: number, s: any) => Math.max(max, parseInt(s.stage ?? 0)), 0);

    const nextStage = maxSentStage > 0 ? maxSentStage + 1 : 0;

    const hasNextStageSession = sessions.some(
        (s: any) => parseInt(s.stage ?? 0) === nextStage
    );

    const showNextStageBooking = nextStage > 0 && !hasNextStageSession;

    return (
        <div className="counselling-info-wrap">
            {sessions.length > 0 ? (
                <>
                    {[...sessions]
                        .sort((a, b) => parseInt(a.stage ?? 0) - parseInt(b.stage ?? 0))
                        .map((session: any, idx: number) => {
                            const {
                                id,
                                stage,
                                status,
                                createdAt,
                                appointmentFrom,
                                counsellorId,
                                counsellorName,
                                appointmentLink,
                                sessionLink,
                                sessionType
                            } = session;

                            return (
                                <div key={idx} className="stage-info mb-2">
                                    {/* Counsellor Info */}
                                    {counsellorId && counsellorName ? (
                                        <div className="counsellor-line">
                                            <a
                                                href={`/support/all-bas-report/${counsellorId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <strong>{counsellorName} </strong>
                                            </a>{" "}
                                            (<span className="text-muted">{formatDateOnly(createdAt)}</span>)
                                        </div>
                                    ) : (
                                        <div>NA</div>
                                    )}

                                    <div>
                                        <strong>{`CS${stage}`} </strong>{" "}
                                        <span className="text-muted">
                                            ({formatStatus(status)} -{" "}
                                            {appointmentFrom && <span>{formatDate(appointmentFrom)}</span>} -{" "}
                                            {<span>{sessionType == "onDemand" ? "On-Demand Session" : "Regular Session"}</span>}
                                            )
                                        </span>
                                    </div>

                                    {["SCHEDULED", "BOOKED","RE-SCHEDULED"].includes(status?.toUpperCase()) && (
                                        <div className="mt-1">
                                            <a
                                                href={appointmentLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="me-2"
                                            >
                                                Counsellor Link
                                            </a>
                                            <div className="d-flex align-items-center mt-1">
                                                <a
                                                    href={sessionLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Student Link
                                                </a>
                                                <button
                                                    id={`copy-session-${idx}`}
                                                    className="copy-text border-0 bg-transparent ms-2"
                                                    title="Click to copy session link"
                                                    onClick={() => handleCopyText(sessionLink, `copy-session-${idx}`)}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        fill="currentColor"
                                                        className="bi bi-copy"
                                                        viewBox="0 0 16 16"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {(status?.toUpperCase() === "MISSED" || status?.toUpperCase() === "CANCELLED") && (
                                        <>
                                            {status?.toUpperCase() === "MISSED" && (
                                            <div className="text-danger mt-1">Reschedule Required</div>
                                            )}
                                            {renderBookingLink(bookingUrl, row?.uuid)}
                                        </>
                                    )}


                                    {status?.toUpperCase() === "COMPLETED" && id > 0 && (
                                        <div className="mt-2">
                                            {/* <a
                                                href={`/alumni/${row?.uuid}/profile/sessions`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-conview"
                                            >
                                                SEND SD
                                            </a> */}
                                            <SendMessage basid={id} className="btn btn-gray-de height-40 btn-green" btnText="Send SD" />
                                        </div>
                                    )}  
                                </div>
                            );
                        })}

                    {showNextStageBooking && (
                        <div>
                            <span className="d-block mb-1">
                                <strong>{`CS${nextStage} - Ready to Book`}</strong>
                            </span>
                            {renderBookingLink(bookingUrl, row?.uuid)}
                        </div>
                    )}
                </>
            ) : (
                <div>
                    <span className="d-block mb-1">
                        <strong>CS1 - Ready to Book</strong>
                    </span>
                    {renderBookingLink(bookingUrl, row?.uuid)}
                </div>
            )}
        </div>
    );
};


    const formatStatus = (status: string): string => {
        switch (status) {
            case "BOOKED":
                return "Booked";
            case "MISSED":
                return "Missed";
            case "CANCELLED":
                return "Cancelled";
            case "COMPLETED":
                return "Completed";
            case "READY_TO_BOOK":
                return "Ready to Book";
            case "SD_SENT":
                return "SD SENT";
            default:
                return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase();
        }
    };

    const formatDateOnly = (dateString: string): string => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2);
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatDate = (dateStr: string) => {
      const date = new Date(new Date(dateStr).getTime() + 330 * 60000); // Convert to IST manually
    
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const year = date.getFullYear();
    
      const time = date.toLocaleTimeString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    
      return `${day}-${month}-${year} ${time}`;
    };


    const getConversations = (row: any): any => {
        if (!row?.schoolName || typeof row.schoolName !== 'string') {
            // Gracefully handle missing/invalid schoolName
            return <span style={{ color: 'gray' }}>N/A</span>;
        }
        const parts = row?.schoolName.split(',');
        const schoolNameContent =
            parts.length > 1 ? (
                <>
                    <span>
                        {parts[0]},<br />
                        {parts.slice(1).join(',').trim()}
                    </span>
                </>
            ) : (
                <>{row.schoolName}</>
            );

        if (state.routeInfo.pageName === 'internal-admin-all-school-summary') {
            return (
                <a className="tablink" href={`/support/all-alumni-report/${row.schoolId}`} target="_blank" rel="noopener noreferrer">
                    {schoolNameContent}
                </a>
            );
        }
        // If condition is not satisfied, display just the school name text
        return schoolNameContent;
    };
    

    const getFieldNode = (control: FormControl, row: any) => {
        let content;

        switch (control.id) {
            case 'studentName':
                content = getAlumniNameField(row);
                break;
            case 'campus':
                content = getShortenedSchoolName(row);
                break;
            case 'parentInfo':
                content = getParentInfo(row);
                break;
            case 'testApplicable':
                content = getTestApplicable(row);
                break;
            case 'counsellingInfo':
                content = getCounsellingInfo(row);
                break;
            case 'conversations':
                content = getConversations(row);
                break;
            case 'buttons':
                content = getActionNode(control, row);
                break;
            case 'createdAt':
            case 'updatedAt':
                content = getFormattedDate(row[control.id]);
                break;
            default:
                if (control.props?.domainCategoryCode?.length > 0)
                    content = getDomainValueForCode(row[control.id], control.props.domainCategoryCode, state);
                else content = row[control.id];
        }

        return (
            <td key={control.id} className={control.className}>
                {content}
            </td>
        );
    };

    const handlePerformAction = async (action: string, students: any) => {
        switch (action) {
            case 'UPDATE_3C':
                setSelectedRows([]);
                if (students.length > 0 && students[0].uuid) {
                    window.open(`/alumni/${students[0].uuid}/profile/preference`, '_blank', 'noopener,noreferrer');
                }
                break;
            case 'SCHEDULE':
            case 'RE-SCHEDULE':
                setSelectedRows([]);
                if (students.length > 0 && students[0].uuid) {
                    window.open(`/alumni/${students[0].uuid}/bas`, '_blank', 'noopener,noreferrer');
                }
                break;
            case 'ON-DEMAND_SESSION':
                setSelectedRows([]);
                if (students.length > 0 && students[0].uuid) {
                    function generateTimeOptions(): string {
                        const times: string[] = [];
                        const now = new Date();
                        // const currentHour = now.getHours();
                        const currentMinute = now.getMinutes();
                        const today = now.toISOString().split('T')[0];
                        
                        const dateInput = document.getElementById('date') as HTMLInputElement;
                        const selectedDate = dateInput ? dateInput.value : today;
                        
                        // Shift timings
                        const shiftStartsAtHour = 8;
                        const shiftStartsAtMinute = 0;
                        const shiftEndsAtHour = 21;
                        const shiftEndsAtMinute = 15;
                        
                        // Check if selected date is today
                        const isToday = selectedDate === today;
                        // Calculate the time threshold: 45 minutes before the current time
                        const timeThreshold = now.setMinutes(currentMinute - 45);
                        const thresholdHour = new Date(timeThreshold).getHours();
                        const thresholdMinute = new Date(timeThreshold).getMinutes();
                        // Generate times for each hour in the shift
                        for (let hour = shiftStartsAtHour; hour <= shiftEndsAtHour; hour++) {
                            let minuteStart = 0;
                            let minuteEnd = 55;
                            
                            if (hour === shiftStartsAtHour) {
                                minuteStart = shiftStartsAtMinute;
                            }
                            
                            if (hour === shiftEndsAtHour) {
                                minuteEnd = shiftEndsAtMinute;
                            }
                            
                            for (let minute = minuteStart; minute <= minuteEnd; minute += 5) {
                                // If this is today, filter out FUTURE times (only allow PAST times)
                                if (isToday) {
                                    // if (hour > currentHour || (hour === currentHour && minute > (currentMinute-45))) {
                                    if (hour > thresholdHour || (hour === thresholdHour && minute > thresholdMinute)) {
                                        continue;
                                    }
                                }
                                
                                const hour12 = hour % 12 || 12;
                                const ampm = hour < 12 ? 'AM' : 'PM';
                                const hourStr = hour12.toString().padStart(2, '0');
                                const minuteStr = minute.toString().padStart(2, '0');
                                const displayTime = `${hourStr}:${minuteStr} ${ampm}`;
                                const valueTime = `${hour.toString().padStart(2, '0')}:${minuteStr}`;
                                
                                times.push(`<option value="${valueTime}">${displayTime}</option>`);
                            }
                        }
                        
                        return times.length > 0 ? times.join('') : '<option value="">No available times for selected date</option>';
                    }

                    // Update time options function (for the event listener)
                    function updateTimeOptions(): void {
                        const timeSelect = document.getElementById('time') as HTMLSelectElement;
                        if (timeSelect) {
                            timeSelect.innerHTML = generateTimeOptions();
                        }
                    }
                    Swal.fire({
                        title: 'On-Demand Session',
                        html: `
                            <div style="text-align: left;">
                                <div class="row g-2 mx-0">
                                    <div class='col-12 d-flex flex-column'>
                                        <label class="font-16 font-500" for="counsellor">Counsellor:</label>
                                        <select id="counsellor" class="swal2-input form-select">
                                            <option value="">Select Counsellor</option>
                                            ${counsellors.map((counsellor: any) => `<option value="${counsellor.id}">${counsellor.first_name} ${counsellor.last_name}</option>`).join('')}
                                        </select>
                                    </div>
                                    <div class='col-6 d-flex flex-column'>
                                        <label class="font-16 font-500" for="date">Session Date:</label>
                                        <input class='form-control' type="date" id="date">
                                    </div>
                                    <div class='col-6 d-flex flex-column'>
                                        <label class="font-16 font-500" for="time">Session Start Time:</label>
                                        <select class='form-control' id="time">
                                            <option value="">Select Time</option>
                                            ${generateTimeOptions()}
                                        </select>
                                    </div>
                                    <!-- <small class="text-muted mt-1">
                                        ⓘ Times are in 5-minute intervals
                                    </small> -->
                                </div>
                            </div>
                        `,
                        showCancelButton: false,
                        showCloseButton: true,
                        allowOutsideClick: false,
                        confirmButtonText: 'Create Session',
                        didOpen: () => {
                            const dateInput = document.getElementById('date') as HTMLInputElement;
                            
                            if (dateInput) {
                                // Set max date to today and default value
                                const today = new Date().toISOString().split('T')[0];
                                dateInput.max = today;
                                dateInput.value = today;
                                
                                // Update time options when date changes
                                dateInput.addEventListener('change', updateTimeOptions);
                            }
                        },
                        preConfirm: () => {
                            const counsellor = (document.getElementById('counsellor') as HTMLSelectElement)?.value;
                            const date = (document.getElementById('date') as HTMLInputElement)?.value;
                            const time = (document.getElementById('time') as HTMLSelectElement)?.value;

                            if (!counsellor) {
                                Swal.showValidationMessage('Please select a counsellor');
                                return false;
                            }

                            if (!date || !time) {
                                Swal.showValidationMessage('Please select session date and time');
                                return false;
                            }

                            // Combine date and time
                            const datetimeString = `${date}T${time}`;
                            const selectedDate = new Date(datetimeString);
                            const now = new Date();

                            if (selectedDate > now) {
                                Swal.showValidationMessage('Cannot select future date/time');
                                return false;
                            }

                            return { 
                                counsellor, 
                                datetime: datetimeString 
                            };
                        }
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            const { counsellor, datetime } = result.value;
                            
                            Swal.fire({
                                title: 'Scheduling Session...',
                                didOpen: () => {
                                    Swal.showLoading();
                                }
                            });

                            console.log(`Scheduled session with ${counsellor} at ${datetime}`);
                            const resp = await axiosClient().post(`${process.env.REACT_APP_BAS_API}/bas/create-on-demand-session`, {
                                studentId: students[0].uuid,
                                counsellorId: counsellor,
                                appointmentDateTime: datetime,
                                createdBy: sessionState?.id
                            });
                            Swal.close();

                            if(resp.status == 200){
                                // Swal.fire('Success', 'On-Demand session created successfully', 'success');
                                Swal.fire({
                                    title: `${resp?.data?.success ? 'Success' : 'Error'}`,
                                    text: `${resp?.data?.message}`,
                                    icon: `${resp?.data?.success ? 'success' : 'error'}`,
                                    confirmButtonText: 'OK'
                                }).then(() => {
                                    if (resp?.data?.success) {
                                        window.open(`/alumni/${students[0].uuid}/profile/sessions`, '_blank', 'noopener,noreferrer');
                                        window.location.reload();
                                    }
                                });
                            } else {
                                Swal.fire('Error', `${resp?.data?.message}`, 'error');
                            }
                            
                        }
                    });

                }
                break;
            // case 'RE-ASSIGN':
            //     await deactivateAlumni(students);
            //     setSelectedRows([]);
            //     BasAdminReportService.search(state.data.searchCriteria, state.routeInfo.pageName).then((response: any) => {
            //         setStateValue('alumniList', response.data);
            //         fetchAlumniCount(state);
            //         toast.success('De-activated successfully');
            //     });
            //     break;
            case 'VIEW_HISTORY':
                setSelectedRows([]);
                // Open the profile page of the first student (or handle multiple as needed)
                if (students.length > 0 && students[0].uuid) {
                    window.open(`/alumni/${students[0].uuid}/profile/counselling-logs`, '_blank', 'noopener,noreferrer');
                }
                break;
            case 'VIEW_NOTES':
                setSelectedRows([]);
                // Open the profile page of the first student (or handle multiple as needed)
                if (students.length > 0 && students[0].uuid) {
                    window.open(`/alumni/${students[0].uuid}/profile/counsellor-notes`, '_blank', 'noopener,noreferrer');
                }
                break;
            case 'MAKE_STUDENT_/_PARENT_PRIMARY':
                await deactivateAlumni(students);
                setSelectedRows([]);
                BasAdminReportService.search(state.data.searchCriteria, state.routeInfo.pageName).then((response: any) => {
                    setStateValue('alumniList', response.data);
                    fetchAlumniCount(state);
                    toast.success('De-activated successfully');
                });
                break;
            case 'RAISE_REMOVAL_REQUEST':
                await deactivateAlumni(students);
                setSelectedRows([]);
                BasAdminReportService.search(state.data.searchCriteria, state.routeInfo.pageName).then((response: any) => {
                    setStateValue('alumniList', response.data);
                    fetchAlumniCount(state);
                    toast.success('De-activated successfully');
                });
                break;
            case 'DOWNLOAD_TEST_REPORT':
                console.log(students);
                const sessions = students?.[0]?.psychometricSessions?.filter((s: any) => s.testCode !== 1);
                console.log("sessions",sessions)
                // Check if sessions exist and at least one test is completed
                const hasCompletedTest = Array.isArray(sessions) && sessions.some((s: any) => s.testStatus === 'Completed');

                if (!Array.isArray(sessions) || sessions.length === 0 || !hasCompletedTest) {
                    alert("No completed test to download");
                    return;
                }
                downloadExcel(students);
                break;
        }
    };

    const getSelectedTestData = (item: any) => {
        if (item?.key2succeed_json) return  JSON.parse(item.key2succeed_json);
        if (item?.brainwonders_json) return  JSON.parse(item.brainwonders_json);
        return null;
    };

    const fetchSessions = async (uuid: string) => {
        try {
            const response = await BasAdminReportService.fetchTestSessions(uuid);
            if (response?.data?.success && Array.isArray(response.data?.data)) {
                return response.data.data.filter(
                    (s: any) =>
                        s.test_status === "Completed" &&
                        (s.key2succeed_json || s.brainwonders_json)
                );
            }
        } catch (err) {
            console.error("Failed to fetch test sessions for student", uuid, err);
        }
        return []; // default empty array
    };

    const getStudentExcelRow = async (student: any) => {
        const psychometricSessions = await fetchSessions(student.uuid); // await the async call
        console.log("psychometricSessions",psychometricSessions)
        const row: Record<string, any> = {
            Name: student.name,
            UUID: student.uuid,
        };

        psychometricSessions.forEach((session: any) => {
            const testName = session.test_name || `Test ${session.test_type}`;
            const selectedTestData = getSelectedTestData(session);
            if (!selectedTestData) return;
            console.log("selectedTestData",selectedTestData)
            let cellValue = "";
            // Ideal Career Test
            if (selectedTestData.idealcareertest?.category) {
                cellValue = selectedTestData.idealcareertest.category
                    .map((cat: any) => {
                        const categoryId = Object.keys(cat)[0];
                        const score = cat[categoryId];
                        const categoryName = selectedTestData?.exam?.subdomains?.[categoryId] || `Category ${categoryId}`;
                        return `${categoryName}: ${score}`;
                    })
                    .join(", ");
            }

            // Stream Selector Test
            if (selectedTestData.subjectselectortest) {
                const recommended = selectedTestData.subjectselectortest
                    .filter((cat: any) => cat.recommend === "Recommended For You")
                    .map((cat: any) => cat.title)
                    .join(", ");
                if (recommended) cellValue = recommended;
            }

            // Learning Style / Multiple Intelligence Test
            if (selectedTestData.assessment?.scores) {
                cellValue = [...selectedTestData.assessment.scores]
                    .sort((a: any, b: any) => b.score - a.score)
                    .slice(0, 5)
                    .map((cat: any) => `${cat.description} (${cat.name}): ${cat.score.toFixed(2)}`)
                    .join(", ");
            }

            // Personality Style Test
            if (selectedTestData.content?.dimensionScores) {
                let dimensionScoresCellValue = "";
                let careerClustersCellValue = "";

                // 1) Dimension scores (with proper labels) in one line
                const dimensionScores = selectedTestData.content.dimensionScores as Record<string, number>;

                // Explicit key type (no symbol)
                const order = ["I", "E", "S", "N", "T", "F", "J", "P"] as const;
                type DimensionKey = (typeof order)[number];

                const dimensionLabels: Record<DimensionKey, string> = {
                    I: "Introversion",
                    E: "Extraversion",
                    S: "Sensing",
                    N: "Intuition",
                    T: "Thinking",
                    F: "Feeling",
                    J: "Judging",
                    P: "Perceiving",
                };

                dimensionScoresCellValue = order
                    .filter((key) => dimensionScores[key] != null)
                    .map((key) => `${dimensionLabels[key]} (${key}): ${dimensionScores[key]}`)
                    .join(", ");

                cellValue = dimensionScoresCellValue;

                // 2) Career cluster data: Department as key, interest value as value
                const clusters = selectedTestData.careerClusters;

                if (Array.isArray(clusters) && clusters.length > 0) {
                    careerClustersCellValue = clusters
                        .map((cluster: any) => {
                            const department =
                                cluster.department || cluster.name || cluster.clusterCode || "Unknown";

                            const interestValue = cluster?.rating?.rating?.name ?? "";

                            return `${department}: ${interestValue}`;
                        })
                        .join(", ");

                    if (careerClustersCellValue) {
                        cellValue += `; \r\n[Career Clusters] - ${careerClustersCellValue}`;
                    }
                }
            }

            row[testName] = cellValue;
        });

        return row;
    };

    const downloadExcel = async (students: any[]) => {
        if (!Array.isArray(students) || students.length === 0) {
            alert("No data to download");
            return;
        }

        try {
            const excelData = await Promise.all(
                students.map(async (stu) => {
                    try {
                        return await getStudentExcelRow(stu); 
                    } catch (err) {
                        return null; 
                    }
                })
            );
            const filteredData = excelData.filter((row) => row !== null);
            if (filteredData.length === 0) {
                alert("No data available to generate Excel.");
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(filteredData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "TestReport");

            const downloadFileName = `${students[0].uuid}_${moment().format("DD-MM-YYYY_HH-mm-ss")}`;
            XLSX.writeFile(workbook, `${downloadFileName}.xlsx`);
        } catch (err) {
            console.error("Failed to generate Excel", err);
        } finally {
            console.log("Done fetching data.");
        }
    };

    const fetchAlumniCount = async (state: any) => {
        try {
            const basCountData = await BasAdminReportService.searchCount(state.data.searchCriteria, state.routeInfo.pageName);
            dispatch({
                type: 'SET_INTERNAL_STATE',
                payload: { key: 'basCount', value: basCountData?.data },
            });
        } catch (error) {
            console.error('Error fetching alumni count:', error);
        }
    };

    const showStar = (row: any) => (row.isNotable && row.isNotable == 1 ? true : false);

    return (
        <>
        <div>
            <div className="ActiveListings_present">
                <div className="d-flex flex-wrap align-items-center mb-3 w-100 justify-content-between mailerLinks-parent">
                    <div className="d-flex flex-wrap align-items-center flex-1 mailerLinks my-2">
                        {config['BULK_ACTION'].length > 0 && (
                            <div className="d-flex justify-content-start align-items-center customselect_parnt">
                                <select
                                    name="complete Class 12"
                                    className="form-select w-120"
                                    onChange={(e) => setBulkAction(e.target.value)}>
                                    <option value={''}>Select</option>
                                    {config['BULK_ACTION'].map((action: string) => (
                                        <option key={action} value={action}>
                                            {getActionLabel(action)}
                                        </option>
                                    ))}
                                </select>
                                <button className="btn btn-darkblue" onClick={() => handlePerformAction(bulkAction, selectedRows)}>
                                    <span className="line-height-16">Apply</span>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="d-flex flex-wrap align-items-center justify-content-end">
                        <h3 className="font-14 font-500 mb-0 me-2">
                            Total {state?.internal?.basCount && state?.internal?.basCount[0]?.totalBas} records
                        </h3>
                    </div>
                </div>
                <div className="table-responsive attendee_dtl min-h-420">
                    <table className={`table ${config.TABLE_CLASS}`} style={config.TABLE_STYLE}>
                        <thead>
                            <tr>
                                {resultsFormConfig.controlGroup.map((field: FormControl, index: number) => (
                                    // <th key={field.props.label} style={[0,1,2].includes(index) ? {'width': config['TABLE_WIDTH'][index]} : {}}>
                                    <th
                                        key={field.props.label}
                                        style={config?.TABLE_WIDTH?.length > index ? { width: config?.TABLE_WIDTH[index] } : {}}>
                                        {/* {!index && ( */}
                                        {!index && config['SHOW_SELECT_ALL'] && (
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.length === allRowsData.length}
                                                id="Antony"
                                                className="form-check-input me-2 mt-0"
                                                onChange={handleSelectAll}
                                            />
                                        )}
                                        {field.props.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageData &&
                                pageData.map((row: any) => (
                                    <tr key={row?.uuid+''+row?.schoolId} className="">
                                        {resultsFormConfig.controlGroup.map((field: FormControl) => getFieldNode(field, row))}
                                    </tr>
                                ))}
                            <tr key="loadMoreRef" ref={loadMoreRef} />
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

                <Modal show={showCloseCaseModal} onHide={() => setShowCloseCaseModal(false)} centered>
        <Modal.Header closeButton>
            <Modal.Title>Close Case</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <label htmlFor="comment">Add Comment</label>
            <textarea
            id="comment"
            className="form-control"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter reason or notes for closing case..."
            />
        </Modal.Body>
        <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowCloseCaseModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleCloseCase}>Submit</button>
        </Modal.Footer>
        </Modal>

       {state?.modalFlags?.addCounsellorSession && (
        <AssignSession />
        )}

        <Modal show={showTestModal} onHide={() => setShowTestModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>{selectedTest}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
               {selectedTest === "Ideal Career Test" && (
                    <>
                        <h3 className='font-18 font-500 mb-3'>Here are your Top 3 Careers</h3>
                        <ul>
                            {selectedTestData?.idealcareertest?.category
                            ?.slice(0, 3) // first 3 categories
                            .map((cat: any, index: number) => {
                                const categoryId = Object.keys(cat)[0]; // e.g., "302"
                                const score = cat[categoryId]; // e.g., 15
                                const categoryName =
                                selectedTestData?.exam?.subdomains?.[categoryId]; // lookup from subdomains

                                return (
                                <li key={index}>
                                    {categoryName || `Category ${categoryId}`} – Score: {score}
                                </li>
                                );
                            })}
                        </ul>
                    </>
                )}
                {selectedTest === "Stream Selector Test" && (
                    <>
                        <h3 className="font-18 font-500 mb-3">Stream Preference Order : Recommended For You</h3>
                        <ul>
                        {selectedTestData?.subjectselectortest
                            ?.filter((cat: any) => cat.recommend === "Recommended For You") // only recommended ones
                            .map((cat: any, index: number) => (
                            <li key={index}>
                                {cat.title} 
                            </li>
                            ))}
                        </ul>
                    </>
                )}
                {(selectedTest === "Learning Style Test" || selectedTest === "Multiple Intelligence Test") && (
                    <>
                        <h3 className="font-18 font-500 mb-3">Here are your Top 5 Scores</h3>
                        <ul>
                        {selectedTestData?.assessment?.scores
                            ? [...selectedTestData.assessment.scores] // make a shallow copy
                                .sort((a, b) => b.score - a.score) // sort descending
                                .slice(0, 5) // take top 5
                                .map((cat: any, index: number) => (
                                <li key={index}>
                                    {cat.description} ({cat.name}) : {cat.score.toFixed(2)}
                                </li>
                                ))
                            : null}
                        </ul>
                    </>
                )}
                {selectedTest === "Personality Style Test" && (
  <>
                    <h3 className="font-18 font-500 mb-3">Your Personality Style Results</h3>

                    {/* Personality Type */}
                    <p className="mb-3">
                    <strong>Personality Type:</strong>{" "}
                    {selectedTestData?.assessment?.personalityType}
                    </p>

                    {/* Dimension Scores */}
                    {selectedTestData?.content?.dimensionScores && (
                    <div className="mb-3">
                        <h4 className="font-16 font-500 mb-2">Dimension Scores</h4>
                        <div className="row">
                            <div className="col-md-6">
                                <p>Introversion (I): {selectedTestData.content.dimensionScores.I}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Extraversion (E): {selectedTestData.content.dimensionScores.E}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Sensing (S): {selectedTestData.content.dimensionScores.S}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Intuition (N): {selectedTestData.content.dimensionScores.N}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Thinking (T): {selectedTestData.content.dimensionScores.T}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Feeling (F): {selectedTestData.content.dimensionScores.F}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Judging (J): {selectedTestData.content.dimensionScores.J}</p>
                            </div>
                            <div className="col-md-6">
                                <p>Perceiving (P): {selectedTestData.content.dimensionScores.P}</p>
                            </div>
                        </div>
                    </div>

                    )}

                    {/* Career Cluster Insights */}
                    {Array.isArray(selectedTestData?.careerClusters) &&
                    selectedTestData.careerClusters.length > 0 && (
                        <div className="mb-3">
                        <h4 className="font-16 font-500 mb-2">Career Cluster Insights</h4>
                        <ul>
                            {selectedTestData.careerClusters
                            // enrich with rating value for sorting
                            .map((cluster: any) => ({
                                ...cluster,
                                _ratingValue: cluster?.rating?.rating?.value ?? 0,
                            }))
                            // sort by rating value (highest interest first)
                            .sort((a: any, b: any) => b._ratingValue - a._ratingValue)
                            // limit to top 5 clusters
                            .slice(0, 5)
                            .map((cluster: any, index: number) => (
                                <li key={index}>
                                {cluster.name}{" "}
                                {/* {typeof cluster.clusterCode !== "undefined" &&
                                    `(Code: ${cluster.clusterCode})`} */}
                                {" — "}
                                {cluster?.rating?.rating?.name}{" "}
                                {/* {cluster?.rating?.rating?.value != null &&
                                    `(${cluster.rating.rating.value})`} */}
                                {/* {Array.isArray(cluster.careers) &&
                                    ` • Careers suggested: ${cluster.careers.length}`} */}
                                </li>
                            ))}
                        </ul>
                        </div>
                    )}
                </>
                )}


            </Modal.Body>
            </Modal>

      </>
    );
};

export default Results;
